import { useEffect, useState } from "react";

import SetupMeetingBanner from "components/setup-meeting-banner/setup-meeting-banner";
import SetupMeetingModal from "components/setup-meeting-modal/setup-meeting-modal";
import { AuthorityClaimStatus, isAuthorityRole } from "constants/field-values";
import { useGetOwnClaimsWithSetupPendingQuery } from "graphql/queries/get-own-claims-with-setup-pending.generated";
import { useUpdateClaimSetupMeetingPromptedAtMutation } from "graphql/mutations/update-claim-setup-meeting-prompted-at.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useSetupMeetingBannerDismissed } from "state/reactive-vars/setup-meeting-banner-var";

/**
 * Asks the user to book the 30-minute setup meeting from the Ollie dashboard.
 *
 * It shows for authority users who created their account with the /claim flow (a COMPLETED claim
 * with their user id) while jurisdictions.setup_completed is false. An admin sets setup_completed
 * on the Jurisdiction Claims screen after the meeting, and then nothing shows for any user of that
 * jurisdiction. Other roles do not run the query.
 *
 * 1. The modal opens by itself only while authority_claims.setup_meeting_prompted_at is NULL.
 *    Closing the modal or clicking its Schedule button saves that date.
 * 2. The banner shows after every page reload and every sign in. Its button opens the modal again.
 *    Closing the banner hides it until the next page reload or sign in.
 */
const SetupMeetingPrompt = () => {
	const { loggedUserValue } = useLoggedUser();
	const { isSetupMeetingBannerDismissed, dismissSetupMeetingBanner } = useSetupMeetingBannerDismissed();
	const [isModalOpen, setIsModalOpen] = useState(false);

	const isAuthorityUser = isAuthorityRole(loggedUserValue?.role);

	// network-only: when an admin marks the setup as completed, the next visit to the dashboard must
	// not use the cached claim.
	const { data: claimsWithSetupPendingData } = useGetOwnClaimsWithSetupPendingQuery({
		variables: { status: AuthorityClaimStatus.COMPLETED },
		skip: !isAuthorityUser,
		fetchPolicy: "network-only"
	});
	const [updateClaimSetupMeetingPromptedAt] = useUpdateClaimSetupMeetingPromptedAtMutation();

	const claimWithSetupPending = claimsWithSetupPendingData?.authority_claims[0];
	const isModalNeverPrompted = !!claimWithSetupPending && !claimWithSetupPending.setup_meeting_prompted_at;

	useEffect(() => {
		if (isModalNeverPrompted) setIsModalOpen(true);
	}, [isModalNeverPrompted]);

	// Saves the date only once. If the save fails, the modal opens by itself again on the next
	// visit, so the user does not need an error message.
	const saveModalPromptedAt = async () => {
		if (!claimWithSetupPending || !isModalNeverPrompted) return;

		try {
			const result = await updateClaimSetupMeetingPromptedAt({
				variables: { id: claimWithSetupPending.id, promptedAt: new Date().toISOString() }
			});

			// A row outside the permission filter comes back as null without an error.
			if (!result.data?.update_authority_claims_by_pk) {
				console.error(
					`[SetupMeeting] saveModalPromptedAt: authority_claims row ${claimWithSetupPending.id} was not updated`
				);
			}
		} catch (error) {
			console.error(
				`[SetupMeeting] saveModalPromptedAt: could not save setup_meeting_prompted_at for claim ${claimWithSetupPending.id}`,
				error
			);
		}
	};

	// Also used by the Schedule button of the modal. The booking page opens in a new tab, so the
	// modal is already closed when the user comes back to the dashboard.
	const closeModal = () => {
		setIsModalOpen(false);
		saveModalPromptedAt();
	};

	if (!claimWithSetupPending) return null;

	return (
		<>
			{!isSetupMeetingBannerDismissed && (
				<SetupMeetingBanner onScheduleClick={() => setIsModalOpen(true)} onClose={dismissSetupMeetingBanner} />
			)}
			<SetupMeetingModal isOpen={isModalOpen} onClose={closeModal} onScheduleClick={closeModal} />
		</>
	);
};

export default SetupMeetingPrompt;
